import { MutableRefObject, useEffect, useRef, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

export const useTemplates = (
  contentType: string,
  isOpen: boolean,
  itemId: string | undefined,
  savedTemplateNameRef: MutableRefObject<string | null>
) => {
  const [templates, setTemplates] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateMetaParams, setTemplateMetaParams] = useState<string[]>([]);
  const previousTemplateRef = useRef<string>('');

  // Fetch templates when contentType is available
  useEffect(() => {
    if (!isOpen || !itemId) {
      setTemplates([]);
      setSelectedTemplate('');
      setLoadingTemplates(false);
      return;
    }

    if (!contentType) {
      setTemplates([]);
      setSelectedTemplate('');
      return;
    }

    const abortController = new AbortController();

    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const response = await api.get('/mxm/posts/templates', {
          params: { category: contentType },
          signal: abortController.signal
        });

        if (abortController.signal.aborted) return;

        if (response.data?.templates && Array.isArray(response.data.templates)) {
          setTemplates(response.data.templates);

          // Use saved template if it exists in the list
          if (savedTemplateNameRef.current && response.data.templates.includes(savedTemplateNameRef.current)) {
            previousTemplateRef.current = savedTemplateNameRef.current;
            setSelectedTemplate(savedTemplateNameRef.current);
            fetchTemplateMeta(savedTemplateNameRef.current);
          } else if (response.data.templates.length > 0) {
            const firstTemplate = response.data.templates[0];
            previousTemplateRef.current = firstTemplate;
            setSelectedTemplate(firstTemplate);
            fetchTemplateMeta(firstTemplate);
          } else {
            previousTemplateRef.current = '';
            setSelectedTemplate('');
            setTemplateMetaParams([]);
          }
        } else {
          setTemplates([]);
          previousTemplateRef.current = '';
          setSelectedTemplate('');
          setTemplateMetaParams([]);
        }
      } catch (error: unknown) {
        if (getErrorMessage(error) === 'canceled') {
          return;
        }
        console.error('Failed to fetch templates:', error);
        setTemplates([]);
        setSelectedTemplate('');
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingTemplates(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      abortController.abort();
    };
  }, [contentType, isOpen, itemId, savedTemplateNameRef]);

  const fetchTemplateMeta = async (templateName?: string, postId?: string | number) => {
    if (!contentType) {
      setTemplateMetaParams([]);
      return null;
    }

    try {
      const params: any = { category: contentType };
      if (templateName) {
        params.templateName = templateName;
      }
      if (postId) {
        params.postId = postId;
      }
      const response = await api.get('/mxm/posts/template-meta', { params });
      const paramsList = response.data?.params ? Object.keys(response.data.params) : [];
      setTemplateMetaParams(paramsList);
      return response.data;
    } catch (error: unknown) {
      console.error('Failed to fetch template meta:', error);
      setTemplateMetaParams([]);
      return null;
    }
  };

  const shouldShowField = (field: string) => {
    return templateMetaParams.length === 0 || templateMetaParams.includes(field);
  };

  return {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    loadingTemplates,
    templateMetaParams,
    previousTemplateRef,
    fetchTemplateMeta,
    shouldShowField,
  };
};

